import { Modal, ScrollArea, Stack, Table, Tabs, Text } from '@mantine/core';
import { formatTimestampToDateTimeWithMs } from '../../../../../utils/formatDuration';
import type { RepairPreview } from '../types';
import { formatRepairMetric, formatRepairPercent } from '../utils/formatRepair';

type SessionRepairPreviewModalProps = {
  opened: boolean;
  sessionId: number | null;
  preview: RepairPreview | null;
  onClose: () => void;
};

export function SessionRepairPreviewModal({
  opened,
  sessionId,
  preview,
  onClose,
}: SessionRepairPreviewModalProps) {
  if (!preview || sessionId == null) {
    return (
      <Modal opened={opened} onClose={onClose} title='Riparazioni' centered />
    );
  }

  const repairedRecordsCount = preview.repairedEvents.filter(event =>
    Boolean(event.repair || event.type === 'gap')
  ).length;
  const gapAddedCount = preview.repairedEvents.filter(
    event => event.type === 'gap' || event.repair?.type === 'gap'
  ).length;
  const repairedPercent =
    preview.repairedCount > 0
      ? (repairedRecordsCount / preview.repairedCount) * 100
      : 0;
  const duplicateAdjustedCount = repairedRecordsCount - gapAddedCount;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Riparazioni sessione ${sessionId}`}
      size='90%'
      centered
    >
      <Stack gap='md'>
        <Text size='sm' c='dimmed'>
          Eventi originali: {preview.originalCount} | Eventi finali dopo
          riparazione: {preview.repairedCount}
        </Text>

        <Tabs defaultValue='repairs'>
          <Tabs.List>
            <Tabs.Tab value='repairs'>Riparazioni effettuate</Tabs.Tab>
            <Tabs.Tab value='final-records'>Record riparato</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value='repairs' pt='md'>
            <ScrollArea h='60vh' type='auto'>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Timestamp</Table.Th>
                    <Table.Th>Tipo</Table.Th>
                    <Table.Th>Dettaglio</Table.Th>
                    <Table.Th>HR</Table.Th>
                    <Table.Th>HRV</Table.Th>
                    <Table.Th>LF</Table.Th>
                    <Table.Th>HF</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {preview.repairedOnly.map(event => (
                    <Table.Tr key={`${event.timestamp}-${event.repair?.type}`}>
                      <Table.Td>
                        {formatTimestampToDateTimeWithMs(event.timestamp)}
                      </Table.Td>
                      <Table.Td>{event.repair?.type}</Table.Td>
                      <Table.Td>{event.repair?.summary}</Table.Td>
                      <Table.Td>{formatRepairMetric(event.hr)}</Table.Td>
                      <Table.Td>{formatRepairMetric(event.hrv)}</Table.Td>
                      <Table.Td>{formatRepairMetric(event.lf)}</Table.Td>
                      <Table.Td>{formatRepairMetric(event.hf)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Tabs.Panel>

          <Tabs.Panel value='final-records' pt='md'>
            <Stack gap='sm' mb='sm'>
              <Text size='sm' c='dimmed'>
                Eventi originali:{' '}
                <Text span fw={600}>
                  {preview.originalCount}
                </Text>{' '}
                | Eventi finali dopo riparazione:{' '}
                <Text span fw={600}>
                  {preview.repairedCount}
                </Text>{' '}
                | Eventi riparati nel record finale:{' '}
                <Text span c='green' fw={700}>
                  {repairedRecordsCount} ({formatRepairPercent(repairedPercent)}
                  )
                </Text>
                .{' '}
                {gapAddedCount > 0 ? (
                  <>
                    Sono stati aggiunti{' '}
                    <Text span c='green' fw={600}>
                      {gapAddedCount}
                    </Text>{' '}
                    eventi per colmare i gap
                    {duplicateAdjustedCount > 0
                      ? ` e consolidati ${duplicateAdjustedCount} record duplicati.`
                      : '.'}
                  </>
                ) : duplicateAdjustedCount > 0 ? (
                  <>
                    Sono stati consolidati{' '}
                    <Text span c='green' fw={600}>
                      {duplicateAdjustedCount}
                    </Text>{' '}
                    record duplicati.
                  </>
                ) : (
                  ' Non sono stati applicati inserimenti o consolidamenti.'
                )}
              </Text>
            </Stack>

            <ScrollArea h='50vh' type='auto'>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Timestamp</Table.Th>
                    <Table.Th>Tipo</Table.Th>
                    <Table.Th>HR</Table.Th>
                    <Table.Th>HRV</Table.Th>
                    <Table.Th>LF</Table.Th>
                    <Table.Th>HF</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {preview.repairedEvents.map(event => (
                    <Table.Tr
                      key={`${event.timestamp}-${event.repair?.type ?? 'raw'}`}
                      style={
                        event.repair || event.type === 'gap'
                          ? { backgroundColor: 'rgba(34, 197, 94, 0.08)' }
                          : undefined
                      }
                    >
                      <Table.Td>
                        {formatTimestampToDateTimeWithMs(event.timestamp)}
                      </Table.Td>
                      <Table.Td>
                        {event.repair?.type ?? event.type ?? '—'}
                      </Table.Td>
                      <Table.Td>{formatRepairMetric(event.hr)}</Table.Td>
                      <Table.Td>{formatRepairMetric(event.hrv)}</Table.Td>
                      <Table.Td>{formatRepairMetric(event.lf)}</Table.Td>
                      <Table.Td>{formatRepairMetric(event.hf)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Modal>
  );
}
